import { message } from 'antd';
import React, { useCallback, useRef, useState } from 'react';

type AcceptRule = {
  type: 'accept';
  accept: string[];
  message: string;
};

export type ValidatorRule = {
  type: 'validator';
  validator: (files: FileList) => Promise<boolean | File[]> | boolean | File[];
};

export type DraggerRule = AcceptRule | ValidatorRule;

interface DraggerProps {
  rules?: DraggerRule[];
  onFileUpload: (files: FileList) => Promise<void> | void;
  children: React.ReactNode;
  className?: string;
  onDragStatusChange?: (isDragOver: boolean) => void;
  multiple?: boolean;
}

function isValidAccept(file: File, acceptTypes: string[]): boolean {
  if (acceptTypes.length === 0) return true;

  if (file.type) {
    return acceptTypes.some((type) => file.type === type);
  }

  // 如果文件没有 type，则检查文件扩展名
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  return acceptTypes
    .filter((type) => type.startsWith('.'))
    .some((type) => fileExtension === type.slice(1).toLowerCase());
}

const Dragger: React.FC<DraggerProps> = React.memo(
  ({
    rules = [],
    onFileUpload,
    children,
    className = '',
    onDragStatusChange,
    multiple = false,
  }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // 获取 accept 规则
    const acceptRule = rules.find((rule): rule is AcceptRule => rule.type === 'accept');
    const acceptString = acceptRule?.accept.join(',') || '';

    // 验证文件
    const validateFiles = useCallback(
      async (files: FileList): Promise<{ isValid: boolean; validFiles?: FileList }> => {
        let currentFiles = Array.from(files);

        // 验证 accept
        if (acceptRule) {
          const validFiles = currentFiles.filter((file) =>
            isValidAccept(file, acceptRule.accept),
          );

          if (validFiles.length === 0) {
            message.warning(acceptRule.message);
            return { isValid: false };
          }
          currentFiles = validFiles;
        }

        // 验证自定义规则
        const validatorRules = rules.filter(
          (rule): rule is ValidatorRule => rule.type === 'validator',
        );

        for (const rule of validatorRules) {
          try {
            const result = await rule.validator(currentFiles as unknown as FileList);

            if (Array.isArray(result)) {
              // 如果返回文件数组，更新当前文件列表
              currentFiles = result;
              if (currentFiles.length === 0) {
                return { isValid: false };
              }
            } else if (!result) {
              // 如果返回 false，直接返回验证失败
              return { isValid: false };
            }
          } catch (error) {
            console.error('Validation error:', error);
            return { isValid: false };
          }
        }

        // 将筛选后的文件数组转换回 FileList
        const dataTransfer = new DataTransfer();
        currentFiles.forEach((file) => dataTransfer.items.add(file));

        return {
          isValid: true,
          validFiles: dataTransfer.files,
        };
      },
      [rules],
    );

    // 处理文件上传
    const handleFiles = useCallback(
      async (files: FileList) => {
        if (!files.length) return;

        const { isValid, validFiles } = await validateFiles(files);
        if (!isValid || !validFiles) return;

        try {
          await onFileUpload(validFiles);
        } catch (error) {
          console.error('File upload error:', error);
        }
      },
      [validateFiles, onFileUpload],
    );

    // 拖拽相关处理器
    const handleDragOver = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isDragOver) {
          setIsDragOver(true);
          onDragStatusChange?.(true);
        }
      },
      [isDragOver, onDragStatusChange],
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        onDragStatusChange?.(false);
      },
      [onDragStatusChange],
    );

    const handleDrop = useCallback(
      async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        onDragStatusChange?.(false);

        await handleFiles(e.dataTransfer.files);
      },
      [handleFiles, onDragStatusChange],
    );

    // 处理点击上传
    const handleInputChange = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;

        await handleFiles(files);

        // 重置 input
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      },
      [handleFiles],
    );

    const handleClick = useCallback(() => {
      inputRef.current?.click();
    }, []);

    return (
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`dragger relative cursor-pointer ${isDragOver ? 'drag-over' : ''} ${className}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptString}
          multiple={multiple}
          className="hidden"
          onChange={handleInputChange}
          onClick={(e) => e.stopPropagation()} // 防止冒泡
        />
        {children}
      </div>
    );
  },
);

Dragger.displayName = 'Dragger';

export default Dragger;
